import { Breadcrumbs, Tag, Text } from '@loadsmart/loadsmart-ui'
import {
  Box,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import mapping from 'atoms/Icon/mapping'
import Loading from 'atoms/Loading'
import SimplePagination from 'atoms/SimplePagination'
import LSDate, { DATE_FORMAT_MMDDYYHHMM } from 'common/Date.helpers'
import { InstallationList } from 'common/types/kraken-core/Installation'
import { PaginatedResponse } from 'common/types/kraken-core/Pagination'
import useDebouncedValue from 'hooks/useDebouncedValue'
import useTopNavigationContext from 'hooks/useTopNavigationContext/useTopNavigationContext'
import { useEffect, useState } from 'react'
import { searchInstallations } from 'services/kraken-core/installations/installations.service'
import InstallationQuickView from './InstallationQuickView'

export interface InstallationDashboardProps {}

export function InstallationDashboard(props: InstallationDashboardProps) {
  const topNavigation = useTopNavigationContext()
  const [installations, setInstallations] = useState<PaginatedResponse<InstallationList>>()
  const [isSearchingnstallations, setIsSearchingInstallations] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(15)
  const [selectedInstallation, setSelectedInstallation] = useState<InstallationList | null>(null)
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500)

  function search() {
    setIsSearchingInstallations(true)
    searchInstallations({
      installation_type: ['kac', 'self_service'],
      name: debouncedSearchTerm,
      page,
      page_size: pageSize,
    })
      .then(response => {
        setInstallations(response.response?.data)
      })
      .finally(() => {
        setIsSearchingInstallations(false)
      })
  }

  function handleOnClickInstallation(installation: InstallationList) {
    // Open a new tab with the installation details
    setSelectedInstallation(installation)
    // window.open(`/installations/${installation.id}`, '_blank')
  }

  useEffect(() => {
    setPage(1)
  }, [debouncedSearchTerm])

  useEffect(() => {
    search()
  }, [debouncedSearchTerm, page, pageSize])

  useEffect(() => {
    // Set the top navigation state
    topNavigation.updateState({
      children: (
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Box display="flex" alignItems="center" gap={1}>
              {mapping['Installation']}
              <Breadcrumbs entries={[{ label: 'Installations', active: true }]} />
            </Box>
          </Grid>
        </Grid>
      ),
    })
  }, [])

  return (
    <Stack spacing={2}>
      <InstallationQuickView
        installationId={selectedInstallation?.id}
        open={selectedInstallation !== null}
        onClose={() => setSelectedInstallation(null)}
      />

      <TextField
        title="Search"
        label="Search by keyword..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        // size="small"
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Text variant="caption-bold" color="color-neutral">
                  Name
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="caption-bold" color="color-neutral">
                  Party
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="caption-bold" color="color-neutral">
                  Counter Party
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="caption-bold" color="color-neutral">
                  Created At
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="caption-bold" color="color-neutral">
                  Updated At
                </Text>
              </TableCell>
              <TableCell>
                <Text variant="caption-bold" color="color-neutral">
                  Active?
                </Text>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isSearchingnstallations && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Loading />
                </TableCell>
              </TableRow>
            )}

            {!isSearchingnstallations && installations && installations.results.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2">No installations found</Typography>
                </TableCell>
              </TableRow>
            )}

            {!isSearchingnstallations &&
              installations &&
              installations.results.map((installation, index) => (
                <TableRow
                  key={index}
                  onClick={() => handleOnClickInstallation(installation)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)', // Adjust hover color as needed
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={'bold'}>
                      {installation.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{installation.party?.name || 'N/A'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {installation.counterparty?.name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {LSDate(installation.created_at)?.format(DATE_FORMAT_MMDDYYHHMM)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {LSDate(installation.updated_at)?.format(DATE_FORMAT_MMDDYYHHMM)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tag variant={installation.is_active ? 'accent' : 'warning'}>
                      {installation.is_active ? 'Active' : 'Inactive'}
                    </Tag>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" width="100%">
        <SimplePagination
          currentPage={page}
          previousPage={() => setPage(prev => prev - 1)}
          nextPage={() => setPage(prev => prev + 1)}
          canPreviousPage={!!installations?.previous}
          canNextPage={!!installations?.next}
          onPage={p => setPage(p)}
          pageSize={pageSize}
          onPageSize={p => setPageSize(p)}
        />
      </Box>
    </Stack>
  )
}
